import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, Chip, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import DashBoardTable from "../reuseable-components/DashBoardTable";
import ConfirmModal from "../reuseable-components/ConfirmModal";
import CreateEditEventModal from "./CreateEditEventModal";
import ViewEventRSVPModal from "./ViewEventRSVPModal";
import {
  createEvent,
  deleteEvent,
  getEventRsvps,
  updateEvent,
} from "../../../apis/admin-apis/dashboard-apis/EventTabApis";

const formatEventDate = (value) => {
  if (!value) {
    return "-";
  }

  const [year, month, day] = value.split("-");

  if (!year || !month || !day) {
    return value;
  }

  return `${day}/${month}/${year}`;
};

const formatEventStatus = (value) => {
  if (!value) {
    return "-";
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
};

const mapEventToRow = (event) => ({
  id: event._id,
  title: event.title || "-",
  type: event.eventType || "-",
  buttonLabel: event.ctaLabel || "Join Us",
  start: `${formatEventDate(event.eventDate)} ${event.eventTime || ""}`.trim(),
  rawEventDate: event.eventDate || "",
  eventDate: formatEventDate(event.eventDate),
  startTime: event.eventTime || "-",
  duration: event.duration ? String(event.duration) : "",
  capacity: event.capacity ? String(event.capacity) : "",
  location: event.venueName || "-",
  venueName: event.venueName || "-",
  streetAddress: event.address || "-",
  city: event.city || "-",
  zipCode: event.zipCode || "-",
  description: event.description || "-",
  parkingInfo: event.parkingInfo || "",
  accessibilityInfo: event.accessibilityInfo || "",
  imageUrl: event.imageUrl || "",
  rsvps: event.capacity ? `${event.registered ?? 0} / ${event.capacity}` : `${event.registered ?? 0}`,
  status: formatEventStatus(event.status),
  rawStatus: event.status || "upcoming",
});

const EventsTab = ({ eventsData = [], refreshDashboard }) => {
  // TABLE STATE
  const [sortConfig, setSortConfig] = useState({
    key: "start",
    direction: "asc",
  });

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // MODAL STATE
  const [openModal, setOpenModal] = useState(false);
  const [viewRsvp, setViewRsvp] = useState(false);
  const [eventRsvps, setEventRsvps] = useState([]);
  const [isRsvpsLoading, setIsRsvpsLoading] = useState(false);
  const [isSavingEvent, setIsSavingEvent] = useState(false);
  const [isDeletingEvent, setIsDeletingEvent] = useState(false);

  // EDIT DATA STATE
  const mappedEvents = useMemo(
    () => eventsData.map((event) => mapEventToRow(event)),
    [eventsData],
  );

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState(mappedEvents);
  const [confirmState, setConfirmState] = useState({
    open: false,
    eventId: null,
  });

  useEffect(() => {
    setEvents(mappedEvents);
  }, [mappedEvents]);

  // OPEN CREATE
  const handleOpenCreate = () => {
    setSelectedEvent(null);
    setOpenModal(true);
  };

  // OPEN EDIT
  const handleEdit = (row) => {
    setSelectedEvent(row);
    setOpenModal(true);
  };

  const handleViewRsvps = async (row) => {
    setSelectedEvent(row);
    setViewRsvp(true);
    setIsRsvpsLoading(true);

    try {
      const response = await getEventRsvps(row.id);

      if (response?.success) {
        setEventRsvps(response.rsvps || []);
      } else {
        setEventRsvps([]);
      }
    } catch (error) {
      console.error("Failed to load event RSVPs:", error);
      setEventRsvps([]);
    } finally {
      setIsRsvpsLoading(false);
    }
  };

  const handleOpenDeleteConfirm = (eventId) => {
    setConfirmState({
      open: true,
      eventId,
    });
  };

  const handleCloseConfirm = () => {
    setConfirmState({
      open: false,
      eventId: null,
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmState.eventId) {
      handleCloseConfirm();
      return;
    }

    setIsDeletingEvent(true);

    try {
      const response = await deleteEvent(confirmState.eventId);

      if (response?.success) {
        setEvents((prev) =>
          prev.filter((event) => event.id !== confirmState.eventId),
        );
        if (refreshDashboard) refreshDashboard();
      }
    } catch (error) {
      console.error("Failed to delete event:", error);
    } finally {
      setIsDeletingEvent(false);
      handleCloseConfirm();
    }
  };

  const handleSaveEvent = async (formData) => {
    setIsSavingEvent(true);

    try {
      const payload = {
        title: formData.title,
        eventType: formData.eventType,
        ctaLabel: formData.ctaLabel,
        status: formData.status,
        eventDate: formData.eventDate,
        eventTime: formData.eventTime,
        duration: formData.duration,
        capacity: formData.capacity,
        venueName: formData.venueName,
        address: formData.address,
        city: formData.city,
        zipCode: formData.zipCode,
        description: formData.description,
        parkingInfo: formData.parkingInfo,
        accessibilityInfo: formData.accessibilityInfo,
        imageUrl: formData.imageUrl,
      };

      if (selectedEvent?.id) {
        const response = await updateEvent(selectedEvent.id, payload);
        const updatedEvent = response?.event;

        if (updatedEvent) {
          const updatedRow = mapEventToRow(updatedEvent);
          setEvents((prev) =>
            prev.map((event) =>
              event.id === selectedEvent.id ? updatedRow : event,
            ),
          );
          if (refreshDashboard) refreshDashboard();
        }
      } else {
        const response = await createEvent(payload);
        const createdEvent = response?.event;

        if (createdEvent) {
          setEvents((prev) => [mapEventToRow(createdEvent), ...prev]);
          if (refreshDashboard) refreshDashboard();
        }
      }

      setOpenModal(false);
      setSelectedEvent(null);
    } catch (error) {
      const msg = error?.response?.data?.error || error.message;
      console.error("Failed to save event:", msg);
      alert("Failed to save event: " + msg);
    } finally {
      setIsSavingEvent(false);
    }
  };

  const sortedEvents = useMemo(() => {
    const data = [...events];

    if (sortConfig.key) {
      data.sort((a, b) => {
        const valueA = a[sortConfig.key];
        const valueB = b[sortConfig.key];

        if (valueA < valueB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }

        if (valueA > valueB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }

        return 0;
      });
    }

    return data;
  }, [events, sortConfig]);

  const totalPages = Math.ceil(sortedEvents.length / rowsPerPage) || 1;

  const paginatedEvents = sortedEvents.slice(
    (page - 1) * rowsPerPage,
    (page - 1) * rowsPerPage + rowsPerPage,
  );

  // TABLE ROWS
  const rows = paginatedEvents.map((row) => ({
    ...row,

    event: (
      <Box sx={{ py: 0.5 }}>
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            color: "#0F1E3D",
            lineHeight: 1.2,
          }}
        >
          {row.title}
        </Typography>

        <Typography sx={{ fontSize: "11px", color: "#999" }}>
          {row.type}
        </Typography>
      </Box>
    ),

    status: (
      <Chip
        label={row.status}
        size="small"
        sx={{
          bgcolor: "#E8F5E9",
          color: "#2E7D32",
          fontSize: "11px",
          fontWeight: 600,
          borderRadius: "4px",
          height: "22px",
        }}
      />
    ),

    actions: (
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          size="small"
          sx={{
            bgcolor: "#0F1E3D",
            textTransform: "none",
            fontSize: "11px",
            "&:hover": { bgcolor: "#1a2e5a" },
          }}
          onClick={() => handleViewRsvps(row)}
        >
          View RSVPs
        </Button>

        <Button
          variant="contained"
          size="small"
          onClick={() => handleEdit(row)}
          sx={{
            bgcolor: "#D4A017",
            textTransform: "none",
            fontSize: "11px",
            minWidth: "auto",
            "&:hover": { bgcolor: "#B8860B" },
          }}
        >
          Edit
        </Button>

        <Button
          variant="contained"
          size="small"
          onClick={() => handleOpenDeleteConfirm(row.id)}
          sx={{
            bgcolor: "#FFEAEA",
            color: "#D32F2F",
            textTransform: "none",
            fontSize: "11px",
            minWidth: "auto",
            boxShadow: "none",
            "&:hover": { bgcolor: "#FFDADA" },
          }}
        >
          Delete
        </Button>
      </Stack>
    ),
  }));

  return (
    <>
      <Box
        className="events-tab-container"
        sx={{
          p: {
            xs: 1,
            md: 3,
          },
          bgcolor: "#fff",
        }}
      >
        {/* Header */}
        <Box
          className="events-header"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F1E3D" }}>
            Campaign Events
          </Typography>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{
              bgcolor: "#D4A017",
              "&:hover": { bgcolor: "#B8860B" },
              textTransform: "none",
              borderRadius: "6px",
              fontWeight: 600,
              px: 2,
            }}
          >
            New Event
          </Button>
        </Box>

        <Box sx={{ borderBottom: "1px solid #EAE2D1", mb: 4 }} />

        {/* Table */}
        <Box className="events-table-wrapper">
          <DashBoardTable
            columns={[
              { id: "event", label: "Event", sortable: true },
              { id: "start", label: "Start", sortable: true },
              { id: "location", label: "Location", sortable: true },
              { id: "rsvps", label: "RSVPs", sortable: false },
              { id: "status", label: "Status", sortable: false },
              { id: "actions", label: "Actions", sortable: false },
            ]}
            rows={rows}
            sortConfig={sortConfig}
            onSort={(key) =>
              setSortConfig({
                key,
                direction:
                  sortConfig.key === key && sortConfig.direction === "asc"
                    ? "desc"
                    : "asc",
              })
            }
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(v) => {
              setRowsPerPage(v);
              setPage(1);
            }}
            totalResults={events.length}
          />
        </Box>
      </Box>

      {/* MODAL */}
      <CreateEditEventModal
        open={openModal}
        handleClose={() => {
          setOpenModal(false);
          setSelectedEvent(null);
        }}
        eventData={selectedEvent}
        onSave={handleSaveEvent}
        isSaving={isSavingEvent}
      />
      <ViewEventRSVPModal
        open={viewRsvp}
        handleClose={() => {
          setViewRsvp(false);
          setEventRsvps([]);
        }}
        eventTitle={selectedEvent?.title || "Event RSVPs"}
        rsvps={eventRsvps}
        isLoading={isRsvpsLoading}
      />
      <ConfirmModal
        open={confirmState.open}
        handleClose={handleCloseConfirm}
        onConfirm={handleConfirmDelete}
        title="Delete Event"
        message="Are you sure you want to delete this event? This action cannot be undone."
        confirmLabel="Delete"
        loading={isDeletingEvent}
      />
    </>
  );
};

export default EventsTab;
